import { useState, useCallback, useRef } from 'react'

/**
 * Shared hook for drag-and-drop interactions used by NGN DragDrop and Bowtie questions.
 *
 * Manages a mapping of { zoneId: optionGuid } and provides handlers for:
 * - Click-to-select then click-to-place
 * - HTML5 drag and drop
 * - Keyboard (Enter/Space to select/place, Escape to cancel)
 * - Reset (return all options home)
 *
 * @param {Object}   initialPlacements   Initial { zoneId: optionGuid } from saved answer
 * @param {boolean}  readonly            Whether interactions are disabled
 * @param {Function} canDrop             (optGuid, zoneId) => boolean — optional constraint check
 * @param {Function} onPlacementsChange  (newPlacements) => void — called after every placement change
 */
export function useDragAndDrop({ initialPlacements = {}, readonly = false, canDrop, onPlacementsChange }) {
  const [placements, setPlacements] = useState(initialPlacements)
  const [selectedGuid, setSelectedGuid] = useState(null)
  const [dragOverZone, setDragOverZone] = useState(null)
  const changeCbRef = useRef(onPlacementsChange)
  changeCbRef.current = onPlacementsChange

  const updatePlacements = useCallback((newPlacements) => {
    setPlacements(newPlacements)
    setSelectedGuid(null)
    if (changeCbRef.current) changeCbRef.current(newPlacements)
  }, [])

  const place = useCallback((zoneId, optGuid) => {
    if (readonly) return
    if (canDrop && !canDrop(optGuid, zoneId)) return

    setPlacements(prev => {
      const next = { ...prev }

      // Remove this option from any zone it currently occupies
      for (const key of Object.keys(next)) {
        if (next[key] === optGuid) delete next[key]
      }

      // If zone already has a different option, displace it (goes home by not being in placements)
      next[zoneId] = optGuid

      setSelectedGuid(null)
      if (changeCbRef.current) changeCbRef.current(next)
      return next
    })
  }, [readonly, canDrop])

  const selectOption = useCallback((optGuid) => {
    if (readonly) return
    setSelectedGuid(prev => prev === optGuid ? null : optGuid)
  }, [readonly])

  const handleZoneClick = useCallback((zoneId) => {
    if (readonly || !selectedGuid) return
    place(zoneId, selectedGuid)
  }, [readonly, selectedGuid, place])

  // --- HTML5 Drag & Drop ---

  const handleDragStart = useCallback((e, optGuid) => {
    if (readonly) return
    e.dataTransfer.setData('text/plain', optGuid)
    e.dataTransfer.effectAllowed = 'move'
    setSelectedGuid(optGuid)
  }, [readonly])

  const handleDragEnd = useCallback(() => {
    setSelectedGuid(null)
    setDragOverZone(null)
  }, [])

  const handleDragOver = useCallback((e, zoneId) => {
    e.preventDefault()
    setDragOverZone(zoneId)
  }, [])

  const handleDragLeave = useCallback((e, zoneId) => {
    setDragOverZone(prev => prev === zoneId ? null : prev)
  }, [])

  const handleDrop = useCallback((e, zoneId) => {
    e.preventDefault()
    setDragOverZone(null)
    const optGuid = e.dataTransfer.getData('text/plain')
    if (optGuid) place(zoneId, optGuid)
  }, [place])

  // --- Keyboard ---

  const handleOptionKeyDown = useCallback((e, optGuid) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      selectOption(optGuid)
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      setSelectedGuid(null)
    }
  }, [selectOption])

  const handleZoneKeyDown = useCallback((e, zoneId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleZoneClick(zoneId)
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      setSelectedGuid(null)
    }
  }, [handleZoneClick])

  // --- Reset ---

  const reset = useCallback(() => {
    updatePlacements({})
    setDragOverZone(null)
  }, [updatePlacements])

  // --- Helpers ---

  /** Returns the zone ID an option is currently placed in, or null. */
  const getPlacedZone = useCallback((optGuid) => {
    for (const [zoneId, guid] of Object.entries(placements)) {
      if (guid === optGuid) return zoneId
    }
    return null
  }, [placements])

  /** Returns true if the option is currently placed in any zone. */
  const isPlaced = useCallback((optGuid) => {
    return Object.values(placements).includes(optGuid)
  }, [placements])

  return {
    placements,
    selectedGuid,
    dragOverZone,
    place,
    selectOption,
    handleZoneClick,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleOptionKeyDown,
    handleZoneKeyDown,
    reset,
    getPlacedZone,
    isPlaced,
  }
}
